/**
 * Item Card Component
 * Unified card for displaying both One-Time Jobs and Recurring Schedules
 * Used by Company dashboard for consistent UI across both item types
 */

import type { OneTimeJob, UserProfile } from '../../../shared/types';
import type { RecurringScheduleWithId } from '../services/recurringSchedule.service';
import {
  CARD_STYLE,
  CARD_HEADER_STYLE,
  CARD_CONTENT_STYLE,
  ASSIGNMENT_STATUS_BADGES,
  ONE_TIME_JOB_STATUS_BADGES,
  ALERT_VARIANTS,
  ALERT_BASE_STYLE,
  createBadgeStyle,
  createButtonStyle,
} from '../styles/components';
import { formatCurrency, formatDateTime, formatLocation } from '@shared/utils/formatting';
import { formatRecurrence } from '../services/recurringSchedule.service';
import { AssignmentControls } from './common';
import { CustomSelect } from './common/CustomSelect';

// Type guard to check if item is a OneTimeJob
function isOneTimeJob(item: OneTimeJob | RecurringScheduleWithId): item is OneTimeJob {
  return 'scheduledStart' in item && 'status' in item;
}

// Type guard to check if item is a RecurringSchedule
function isRecurringSchedule(item: OneTimeJob | RecurringScheduleWithId): item is RecurringScheduleWithId {
  return 'recurrenceFrequency' in item && 'isActive' in item;
}

export interface ItemCardProps {
  // The item to display (either a one-time job or recurring schedule)
  item: OneTimeJob | RecurringScheduleWithId;

  // User role context
  viewerRole: 'company' | 'manager';

  // Available users for assignment
  managers?: UserProfile[];
  staff?: UserProfile[];

  // Legacy prop for backwards compatibility (manager view uses this)
  availableUsers?: UserProfile[];

  // Assignment handlers
  onAssignToManager?: (itemId: string, managerId: string) => void;
  onAssignToStaff?: (itemId: string, staffId: string) => void;
  onUnassign?: (itemId: string) => void;
  onUnassignStaff?: (itemId: string, staffId: string) => void;
  onUnassignMyStaff?: (itemId: string, myStaffIds: string[]) => void;

  // One-time job-specific action handlers
  onEditOneTimeJob?: (oneTimeJob: OneTimeJob) => void;
  onCancelOneTimeJob?: (oneTimeJob: OneTimeJob) => void;

  // Schedule-specific action handlers
  onEditSchedule?: (schedule: RecurringScheduleWithId) => void;
  onToggleActive?: (schedule: RecurringScheduleWithId) => void;
  onDeleteSchedule?: (schedule: RecurringScheduleWithId) => void;

  // Assignment state
  isAssigning?: boolean;

  // User lookup helper
  getUserName: (userId: string) => string;

  // Company timezone (IANA string, e.g. "Europe/Dublin")
  companyTimezone?: string;
}

export function ItemCard({
  item,
  viewerRole,
  managers = [],
  staff = [],
  availableUsers = [],
  onAssignToManager,
  onAssignToStaff,
  onUnassign,
  onUnassignStaff,
  onUnassignMyStaff,
  onEditOneTimeJob,
  onCancelOneTimeJob,
  onEditSchedule,
  onToggleActive,
  onDeleteSchedule,
  isAssigning = false,
  getUserName,
  companyTimezone,
}: ItemCardProps) {
  const isOneTimeJobItem = isOneTimeJob(item);
  const isSchedule = isRecurringSchedule(item);

  // Get assignment status badge config
  const assignmentBadgeConfig = ASSIGNMENT_STATUS_BADGES[item.assignmentStatus];

  // Determine card opacity (for paused schedules)
  const cardOpacity = isSchedule && !item.isActive ? 0.6 : 1;

  // Check if one-time job is cancelled (to disable actions)
  const isOneTimeJobCancelled = isOneTimeJobItem && item.status === 'cancelled';

  return (
    <div style={{ ...CARD_STYLE, opacity: cardOpacity }}>
      {/* Header */}
      <div style={CARD_HEADER_STYLE}>
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
            {item.title}
          </h3>
          {item.description && (
            <p style={{ ...CARD_CONTENT_STYLE, marginBottom: '8px' }}>
              {item.description}
            </p>
          )}
          <div style={CARD_CONTENT_STYLE}>
            <p style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#93c5fd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              {formatLocation(item.location.address)}
            </p>

            {/* One-time job-specific: Show scheduled time */}
            {isOneTimeJobItem && (
              <p style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#93c5fd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                {formatDateTime(item.scheduledStart, "en-IE", companyTimezone)} – {formatDateTime(item.scheduledEnd, "en-IE", companyTimezone)}
              </p>
            )}

            {/* Schedule-specific: Show recurrence pattern */}
            {isSchedule && (
              <p style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#93c5fd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
                {formatRecurrence(item)}
              </p>
            )}

            <p style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#93c5fd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
              {formatCurrency(item.hourlyRate)}
            </p>
          </div>
        </div>

        {/* Status Badges */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
          {/* Assignment status badge */}
          <span style={createBadgeStyle(
            assignmentBadgeConfig.backgroundColor,
            assignmentBadgeConfig.color
          )}>
            {assignmentBadgeConfig.label}
          </span>

          {/* One-time job status badge (for cancelled jobs) */}
          {isOneTimeJobItem && item.status === 'cancelled' && (
            <span style={createBadgeStyle(
              ONE_TIME_JOB_STATUS_BADGES.cancelled.backgroundColor,
              ONE_TIME_JOB_STATUS_BADGES.cancelled.color
            )}>
              {item.status}
            </span>
          )}

          {/* Schedule active/paused badge */}
          {isSchedule && (
            <span style={createBadgeStyle(
              item.isActive ? '#dcfce7' : '#f3f4f6',
              item.isActive ? '#166534' : '#6b7280'
            )}>
              {item.isActive ? 'Active' : 'Paused'}
            </span>
          )}
        </div>
      </div>

      {/* Pending assignment banner — shown to manager when job is assigned to them but not yet to staff */}
      {viewerRole === 'manager' && item.assignmentStatus === 'assigned_to_manager' && (!item.userIds || item.userIds.length === 0) && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          backgroundColor: '#fffbeb',
          border: '1px solid #fcd34d',
          borderRadius: '8px',
          padding: '10px 14px',
          fontSize: '13px',
          color: '#92400e',
          fontWeight: 500,
          marginBottom: '4px',
        }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          Assign this job to a staff member as soon as possible
        </div>
      )}

      {/* Assignment Info */}
      {(viewerRole !== 'manager' || (item.userIds && item.userIds.length > 0)) && (
        <div style={{
          ...ALERT_BASE_STYLE,
          backgroundColor: '#f9fafb',
          border: '1px solid #e5e7eb',
          fontSize: '14px',
        }}>
          {item.assignedManagerId && viewerRole !== 'manager' && (
            <p><strong>Manager:</strong> {getUserName(item.assignedManagerId)}</p>
          )}
          {item.userIds && item.userIds.length > 0 && (() => {
            const myStaffIds = viewerRole === 'manager'
              ? item.userIds.filter((uid) => availableUsers.some((s) => s.uid === uid))
              : [];
            return (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <strong>Staff:</strong>
                  {myStaffIds.length > 1 && onUnassignMyStaff && (
                    <button
                      onClick={() => onUnassignMyStaff(item.id, myStaffIds)}
                      disabled={isAssigning}
                      style={{
                        ...createButtonStyle('danger'),
                        fontSize: '12px',
                        padding: '3px 10px',
                        opacity: isAssigning ? 0.5 : 1,
                        cursor: isAssigning ? 'not-allowed' : 'pointer',
                      }}
                    >
                      Unassign All My Staff
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {item.userIds.map((uid) => {
                    const isOwnStaff = myStaffIds.includes(uid);
                    return (
                      <span
                        key={uid}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          backgroundColor: '#f3f4f6',
                          border: '1px solid #e5e7eb',
                          borderRadius: '999px',
                          padding: '2px 10px',
                          fontSize: '13px',
                          color: '#374151',
                        }}
                      >
                        {getUserName(uid)}
                        {isOwnStaff && onUnassignStaff && (
                          <button
                            onClick={() => onUnassignStaff(item.id, uid)}
                            disabled={isAssigning}
                            title={`Remove ${getUserName(uid)}`}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: isAssigning ? 'not-allowed' : 'pointer',
                              padding: '0 2px',
                              color: '#9ca3af',
                              fontSize: '14px',
                              lineHeight: 1,
                              opacity: isAssigning ? 0.5 : 1,
                            }}
                          >
                            ×
                          </button>
                        )}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          {!item.assignedManagerId && (!item.userIds || item.userIds.length === 0) && (
            <p style={{ color: '#6b7280' }}>Not yet assigned</p>
          )}
        </div>
      )}

      {/* Actions (Company view) */}
      {viewerRole === 'company' && (
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Assignment Controls — hidden for cancelled one-time jobs */}
          {!isOneTimeJobCancelled && (managers.length > 0 || staff.length > 0) && (
            <AssignmentControls
              assignedManagerId={item.assignedManagerId}
              userId={'userId' in item ? item.userId : undefined}
              userIds={item.userIds}
              assignmentStatus={item.assignmentStatus}
              managers={managers}
              staff={staff}
              onAssignToManager={(managerId) => onAssignToManager?.(item.id, managerId)}
              onAssignToStaff={(staffId) => onAssignToStaff?.(item.id, staffId)}
              onUnassign={() => onUnassign?.(item.id)}
              isAssigning={isAssigning}
              getUserName={getUserName}
            />
          )}

          {/* One-time job-specific actions */}
          {isOneTimeJobItem && (
            <>
              {onEditOneTimeJob && !isOneTimeJobCancelled && (
                <button
                  onClick={() => onEditOneTimeJob(item)}
                  style={createButtonStyle('info')}
                >
                  Edit
                </button>
              )}
              {onCancelOneTimeJob && !isOneTimeJobCancelled && (
                <button
                  onClick={() => onCancelOneTimeJob(item)}
                  style={createButtonStyle('danger')}
                >
                  Cancel Job
                </button>
              )}
            </>
          )}

          {/* Schedule-specific actions */}
          {isSchedule && (
            <>
              {onEditSchedule && (
                <button
                  onClick={() => onEditSchedule(item)}
                  style={createButtonStyle('info')}
                >
                  Edit
                </button>
              )}
              {onToggleActive && (
                <button
                  onClick={() => onToggleActive(item)}
                  style={item.isActive ? { ...createButtonStyle('secondary'), backgroundColor: '#e5e7eb', color: '#374151' } : createButtonStyle('success')}
                >
                  {item.isActive ? 'Pause' : 'Resume'}
                </button>
              )}
              {onDeleteSchedule && (
                <button
                  onClick={() => onDeleteSchedule(item)}
                  style={createButtonStyle('danger')}
                >
                  Delete
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Actions (Manager view) */}
      {viewerRole === 'manager' && (isOneTimeJobItem || isSchedule) && (
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          {(() => {
            const assignedIds = item.userIds ?? [];
            const unassignedOptions = availableUsers.filter((s) => !assignedIds.includes(s.uid));
            if (availableUsers.length === 0) {
              return (
                <div style={{ ...ALERT_BASE_STYLE, ...ALERT_VARIANTS.warning }}>
                  No staff members in your team. Contact your company administrator to assign staff to you.
                </div>
              );
            }
            return (
              <CustomSelect
                placeholder={assignedIds.length > 0 ? 'Add staff member...' : 'Select staff member...'}
                options={unassignedOptions.length > 0
                  ? unassignedOptions.map((s) => ({ value: s.uid, label: `${s.firstName} ${s.lastName}` }))
                  : [{ value: '', label: 'All staff already assigned', disabled: true }]
                }
                onChange={(staffId) => { if (staffId) onAssignToStaff?.(item.id, staffId); }}
                disabled={isAssigning}
                width="200px"
              />
            );
          })()}
        </div>
      )}

    </div>
  );
}
